import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Form, Input, Modal, Select } from "antd";
import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { updateLeadDetails } from "../../actionCreator/leadListingActionCreater";
import { getUserInfo } from "../../utils/utils";
import { useAlert } from "../../context/AlertProvider";
import {
  formatAndExtractCountryCode,
  handleMobileChange,
  prefixSelector,
} from "../../utils/mobileValidation";
import { countries } from "../../constant/Countries";
import PlaceAutocomplete from "../../common/PlaceAutocomplete";
import { AuthContext } from "../../context/AuthContext";

const { TextArea } = Input;

const UpdateLead = ({ title, defaultValue, fieldName, id, callback }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { showAlert } = useAlert();
  const { isLeadEdit } = useContext(AuthContext);

  const showModal = () => {
    if (isLeadEdit) {
      setIsModalOpen(true);
    } else {
      showAlert(
        "warning",
        "Permission Denied",
        "You do not have permission to update lead."
      );
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      {defaultValue ? (
        <EditOutlined
          className="edit-field-icon heading14px ml10"
          onClick={showModal}
        />
      ) : (
        <PlusOutlined
          className="edit-field-icon heading14px ml10"
          onClick={showModal}
        />
      )}
      <Modal
        title={`Update ${title}`}
        open={isModalOpen}
        centered
        width={400}
        footer={null}
        onOk={handleCancel}
        onCancel={handleCancel}
      >
        <LeadForm
          title={title}
          defaultValue={defaultValue}
          fieldName={fieldName}
          id={id}
          handleCancel={handleCancel}
          callback={callback}
        />
      </Modal>
    </>
  );
};

export default UpdateLead;

const LeadForm = ({
  title,
  defaultValue,
  fieldName,
  id,
  handleCancel,
  callback,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { id: userId } = getUserInfo();
  const { showAlert } = useAlert();
  const [status, setStatus] = useState();
  const [error, setError] = useState();
  const leadStatusListing = useSelector(
    (state) => state.leadListingReducer.leadStatusList
  );

  useEffect(() => {
    const payload =
      fieldName === "fullname"
        ? {
            [fieldName]: defaultValue?.fullname,
            firstname: defaultValue?.firstname,
            lastname: defaultValue?.lastname,
          }
        : fieldName === "status"
          ? {
              status: leadStatusListing?.find(
                (i) => i?.id === defaultValue?.status
              )?.text,
              lostReason: defaultValue?.lostReason,
              comments: defaultValue?.comments,
            }
          : fieldName === "phoneNo"
            ? {
                [fieldName]: defaultValue,
                prefix: formatAndExtractCountryCode(
                  defaultValue?.split("/")?.[0]
                )?.countryLabel,
                phoneNo: formatAndExtractCountryCode(
                  defaultValue?.split("/")?.[0]
                )?.numberWithoutCountryCode,
              }
            : { [fieldName]: defaultValue };
    form.setFieldsValue(payload);
    if (fieldName === "status") {
      setStatus(
        leadStatusListing?.find((i) => i?.id === defaultValue?.status)?.text
      );
    }
  }, [form, fieldName, defaultValue, leadStatusListing]);

  const onFinish = async (values) => {
    const cleanedValues = Object.fromEntries(
      Object.entries(
        fieldName === "fullname"
          ? {
              ...values,
              [fieldName]: values?.firstname + " " + values?.lastname,
            }
          : fieldName === "status"
            ? {
                ...values,
                status: leadStatusListing?.find(
                  (i) => i.text === values?.status
                )?.id,
              }
            : values
      ).map(([key, value]) => [
        key,
        typeof value === "string" ? value?.trim().replace(/\s+/g, " ") : value,
      ])
    );
    const defaultFieldValue =
      fieldName === "fullname"
        ? defaultValue[fieldName]?.trim().replace(/\s+/g, " ")
        : defaultValue;
    if (defaultFieldValue !== cleanedValues[fieldName]) {
      const res = await dispatch(
        updateLeadDetails(
          fieldName === "companyName"
            ? {
                id,
                userId,
                website: values?.website,
                ...cleanedValues,
              }
            : fieldName === "phoneNo"
              ? {
                  id,
                  userId,
                  ...cleanedValues,
                  phoneNo:
                    "+" +
                    countries?.find((d) => d?.label === cleanedValues?.prefix)
                      ?.code +
                    " " +
                    cleanedValues?.phoneNo,
                }
              : { id, userId, ...cleanedValues }
        )
      );
      if (res?.status === 200) {
        showAlert(
          "success",
          `${res?.data?.firstname} ${res?.data?.lastname} Lead updated successfully!`
        );
        callback && callback();
        handleCancel();
        form.resetFields();
        setStatus();
      } else {
        if (res?.response?.status === 403) {
          showAlert(
            "warning",
            "Permission Denied",
            "You do not have permission to update lead."
          );
          handleCancel();
          form.resetFields();
          setStatus();
        } else {
          showAlert("error", res?.response?.data?.title);
          setError(res?.response?.data?.title);
        }
      }
    }
  };

  return (
    <Form
      form={form}
      initialValues={
        fieldName === "fullname"
          ? {
              [fieldName]: defaultValue?.fullname,
              firstname: defaultValue?.firstname,
              lastname: defaultValue?.lastname,
            }
          : {
              [fieldName]:
                fieldName === "status"
                  ? leadStatusListing?.find((i) => i?.id === defaultValue)?.text
                  : defaultValue,
            }
      }
      layout="vertical"
      className="mt20"
      onFinish={onFinish}
    >
      {error && <p className="text-error text-center pb-2">{error}</p>}
      {["fullname", "phoneNo"]?.includes(fieldName) ? (
        <Form.Item
          label={fieldName === "fullname" ? "First Name" : title}
          name={fieldName === "fullname" ? "firstname" : fieldName}
          normalize={(value) =>
            fieldName === "phoneNo"
              ? value.replace(/[^\d+\- ()]/g, "")
              : value?.replace(/^\s+/, "")
          }
          rules={[
            { required: true, message: "This field is required" },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                if (fieldName === "phoneNo") {
                  const digitCount = (value.match(/\d/g) || []).length;

                  if (value.length > 20) {
                    return Promise.reject("Please enter a valid phone number");
                  }

                  if (digitCount > 15) {
                    return Promise.reject("Please enter a valid phone number");
                  }

                  return Promise.resolve();
                } else {
                  return Promise.resolve();
                }
              },
            },
          ]}
        >
          {fieldName === "phoneNo" ? (
            <Input
              className="common-input"
              addonBefore={prefixSelector()}
              maxLength={20}
              onChange={(e) => handleMobileChange(e.target.value, form)}
            />
          ) : (
            <Input className="common-input" />
          )}
        </Form.Item>
      ) : fieldName === "status" ? (
        <>
          <Form.Item
            label={title}
            name={fieldName}
            normalize={(value) => value?.replace(/^\s+/, "")}
            rules={[{ required: true, message: "This field is required" }]}
          >
            <Select
              placeholder="Select Status"
              options={leadStatusListing?.map((d) => ({
                label: d.text,
                value: d.text,
              }))}
              onChange={(value) => {
                form.setFieldsValue({
                  [fieldName]: value,
                });
                setStatus(value);
              }}
            />
          </Form.Item>
          {status === "Closed Lost" && (
            <>
              <Form.Item
                name="lostReason"
                label="Lost Reason"
                rules={[{ required: true, message: "Please add a reason" }]}
              >
                <Input className="common-input" placeholder="Enter reason" />
              </Form.Item>

              <Form.Item name="comments" label="Comments">
                <TextArea
                  className="common-input"
                  rows={3}
                  placeholder="Enter comments"
                />
              </Form.Item>
            </>
          )}
        </>
      ) : (
        <>
          <Form.Item
            label={title}
            name={fieldName}
            normalize={(value) => value?.replace(/^\s+/, "")}
            rules={[{ required: true, message: "This field is required" }]}
          >
            <PlaceAutocomplete
              onPlaceSelect={(v) => {
                form.setFieldsValue({
                  [fieldName]: v?.name,
                  website: v?.website,
                });
              }}
            />
          </Form.Item>
          <Form.Item name="website" label="Website" className="d-none">
            <Input className="common-input" />
          </Form.Item>
        </>
      )}

      {fieldName === "fullname" && (
        <Form.Item
          label="Last Name"
          name="lastname"
          normalize={(value) => value?.replace(/^\s+/, "")}
          rules={[{ required: true, message: "This field is required" }]}
        >
          <Input />
        </Form.Item>
      )}

      <div className="d-flex gap-20 justify-content-end align-items-center">
        <Button
          className="secondary-btn"
          onClick={() => {
            handleCancel();
            form.resetFields();
            setStatus();
          }}
        >
          Cancel
        </Button>
        <Button type="primary" htmlType="submit" className="theme-btn">
          Update
        </Button>
      </div>
    </Form>
  );
};
